import React, { useCallback, useEffect, useState } from "react";
import BaseInput from "../../../../BaseComponents/BaseInput";
import BaseSelect from "../../../../BaseComponents/BaseSelect";
import BackButton from "../../../../BaseComponents/BackButton";
import BaseTimePicker from "../../../../BaseComponents/BaseTimePicker";
import { ShiftOptions, shiftSettingLabel } from "../../../../Constant/HR/ShiftSetting";
import { PlaceHolderFormat } from "../../../../Constant/requireMessage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SelectPlaceHolder, validationMessages } from "../../../../Constant/validation";
import { addShift, editShift, viewShiftSetting } from "../../../../Api/HRshiftSetting";
import { centerApi } from "../../../../Api/common";
import { checkStatusCodeSuccess } from "../../../../Constant/common";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { batchallocationValidation } from "../../../../Constant/BatchAllocation/batchallocation";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { Clock } from "react-feather";
import BaseButton from "../../../../BaseComponents/BaseButton";

const AddEditShift = ({ isOnboarding = false, onSuccess }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();
  const [onboardingCenterId, setOnboardingCenterId] = useState(null);

  useEffect(() => {
    if (id) {
      document.title = shiftSettingLabel.EDITSHIFT;
    } else if (!isOnboarding) {
      document.title = shiftSettingLabel.ADDSHIFT;
    }
  }, [id, isOnboarding]);

  useEffect(() => {
    if (isOnboarding) {
      const savedOnboardingCenter =
        localStorage.getItem("onboarding_center_id") || localStorage.getItem("selectedCenterId");
      if (savedOnboardingCenter) {
        setOnboardingCenterId(Number(savedOnboardingCenter));
      } else {
        const onboardingOrg =
          localStorage.getItem("onboarding_org_id") || localStorage.getItem("organizationId");
        const payload = onboardingOrg
          ? { condition: { organizationId: Number(onboardingOrg) } }
          : {};
        centerApi(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              const firstCenter = resp?.data?.[0]?.id;
              if (firstCenter) {
                setOnboardingCenterId(firstCenter);
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [isOnboarding]);

  const validationSchema = Yup.object().shape({
    shiftType: Yup.string().required(validationMessages.required(shiftSettingLabel.shiftType)),
    shiftName: Yup.string().required(validationMessages.required(shiftSettingLabel.shiftName)),
    inTime: Yup.string().required(validationMessages.required(shiftSettingLabel.in_time)),
    outTime: Yup.string()
      .required(validationMessages.required(shiftSettingLabel.Out_time))
      .test(
        batchallocationValidation.isGreaterLabel,
        shiftSettingLabel.outTimeCValidation,
        function (value) {
          const { inTime } = this.parent;
          if (!inTime || !value) return true;
          const [inH, inM] = inTime.split(":").map(Number);
          const [outH, outM] = value.split(":").map(Number);
          return outH > inH || (outH === inH && outM > inM);
        },
      ),
  });

  // --- options for break time & half day ---
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    value: String(i).padStart(2, "0"),
    label: `${String(i).padStart(2, "0")} Hr`,
  }));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    value: String(i).padStart(2, "0"),
    label: `${String(i).padStart(2, "0")} Min`,
  }));
  const halfDayHourOptions = Array.from({ length: 13 }, (_, i) => ({
    id: i,
    value: String(i),
    label: `${i} Hour${i !== 1 ? "s" : ""}`,
  }));

  const shiftFormik = useFormik({
    initialValues: {
      shiftType: shiftSettingLabel.Fixed,
      shiftName: null,
      inTime: null,
      outTime: null,
      breakTimeHour: "00",
      breakTimeMinute: "00",
      halfDayHour: "4",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const resolvedCenterId = selectedCenterPayload?.center_id ?? onboardingCenterId;
      const payload = {
        center_id: resolvedCenterId,
        shift_type: values.shiftType,
        shift_name: values.shiftName,
        shift_in_time: values.inTime,
        shift_out_time: values.outTime,
        break_time: `${values.breakTimeHour}:${values.breakTimeMinute}:00`,
        half_day_time: `${values.halfDayHour}hour`,
      };
      const apiCall = id ? editShift(id, payload) : addShift(payload);
      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            resetForm();
            if (isOnboarding && onSuccess) {
              onSuccess(resp?.data?.id ?? resp?.data);
            } else {
              navigate("/ShiftSetting");
            }
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => setLoading(false));
    },
  });

  const { setValues } = shiftFormik;

  const fetchData = useCallback(
    (fetchId) => {
      if (!fetchId) return;
      setLoading(true);
      viewShiftSetting(fetchId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            const d = resp?.data;
            const brkParts = d?.break_time ? d.break_time.split(":") : ["00", "00"];
            const halfDayRaw = d?.half_day_time ? d.half_day_time.replace("hour", "") : "4";
            setValues({
              shiftType: d?.shift_type,
              shiftName: d?.shift_name,
              inTime: d?.shift_in_time,
              outTime: d?.shift_out_time,
              breakTimeHour: brkParts[0] || "00",
              breakTimeMinute: brkParts[1] || "00",
              halfDayHour: halfDayRaw,
            });
          } else toast.error(resp?.message);
        })
        .catch((err) => err)
        .finally(() => setLoading(false));
    },
    [setValues, setLoading],
  );

  useEffect(() => {
    fetchData(id);
  }, [fetchData, id]);

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      {!isOnboarding && (
        <div
          className="animate-slide-down"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {id ? shiftSettingLabel.EDITSHIFT : shiftSettingLabel.ADDSHIFT}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {id ? "Update shift details and timing" : "Define a new work shift for your staff"}
            </p>
          </div>
          <BackButton onClick={() => navigate("/ShiftSetting")} />
        </div>
      )}

      {/* Form card */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <div style={{ height: 3, background: "linear-gradient(90deg,#10b981,#06b6d4)" }} />
        <form onSubmit={shiftFormik.handleSubmit} style={{ padding: "24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {/* Shift Type */}
            <BaseSelect
              name="shiftType"
              label={shiftSettingLabel.SHIFTTYPE.replace(" : ", "")}
              options={ShiftOptions}
              placeholder={SelectPlaceHolder(shiftSettingLabel.shiftType)}
              handleChange={(field, value) => shiftFormik.setFieldValue(field, value)}
              handleBlur={() => shiftFormik.setFieldTouched(shiftSettingLabel.shiftTypeKey, true)}
              value={shiftFormik.values.shiftType}
              isDisabled
              touched={shiftFormik.touched.shiftType}
              error={shiftFormik.errors.shiftType}
              required
            />

            {/* Shift Name */}
            <BaseInput
              name="shiftName"
              type="text"
              label={shiftSettingLabel.SHIFTNAME.replace(" :", "")}
              placeholder={PlaceHolderFormat(shiftSettingLabel.shiftNameLabel)}
              value={shiftFormik.values.shiftName}
              touched={shiftFormik.touched.shiftName}
              error={shiftFormik.errors.shiftName}
              handleBlur={shiftFormik.handleBlur}
              handleChange={shiftFormik.handleChange}
              required
            />
          </div>

          {/* Shift Time */}
          <div style={{ marginTop: 20 }}>
            <label
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "var(--text-primary)",
                marginBottom: 8,
                display: "block",
              }}
            >
              {shiftSettingLabel.SHIFTTIME.replace(" :", "")}{" "}
              <span style={{ color: "#f43f5e" }}>*</span>
            </label>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <BaseTimePicker
                  name="inTime"
                  value={shiftFormik.values.inTime}
                  onChange={(v) => shiftFormik.setFieldValue(shiftSettingLabel.inTime, v)}
                  onBlur={shiftFormik.handleBlur}
                  touched={shiftFormik.touched.inTime}
                  error={shiftFormik.errors.inTime}
                  placeholder="Select in-time"
                  required
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: 8,
                  color: "var(--text-muted)",
                  fontWeight: 600,
                }}
              >
                {shiftSettingLabel.TO}
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <BaseTimePicker
                  name="outTime"
                  value={shiftFormik.values.outTime}
                  onChange={(v) => shiftFormik.setFieldValue(shiftSettingLabel.outTime, v)}
                  onBlur={shiftFormik.handleBlur}
                  touched={shiftFormik.touched.outTime}
                  error={shiftFormik.errors.outTime}
                  placeholder="Select out-time"
                  required
                />
              </div>
            </div>
          </div>

          {/* Break Time & Half Day */}
          <div
            style={{
              marginTop: 20,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {/* Break Time */}
            <div
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 14,
                padding: "16px 18px",
              }}
            >
              <label
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: "rgba(16,185,129,0.15)",
                  }}
                >
                  <Clock size={13} strokeWidth={2.5} style={{ color: "#10b981" }} />
                </span>
                {shiftSettingLabel.BREAKTIME}
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <BaseSelect
                    name="breakTimeHour"
                    label="Hours"
                    options={hourOptions}
                    placeholder="HH"
                    handleChange={(field, value) => shiftFormik.setFieldValue(field, value)}
                    handleBlur={() => shiftFormik.setFieldTouched("breakTimeHour", true)}
                    value={shiftFormik.values.breakTimeHour}
                    touched={shiftFormik.touched.breakTimeHour}
                    error={shiftFormik.errors.breakTimeHour}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <BaseSelect
                    name="breakTimeMinute"
                    label="Minutes"
                    options={minuteOptions}
                    placeholder="MM"
                    handleChange={(field, value) => shiftFormik.setFieldValue(field, value)}
                    handleBlur={() => shiftFormik.setFieldTouched("breakTimeMinute", true)}
                    value={shiftFormik.values.breakTimeMinute}
                    touched={shiftFormik.touched.breakTimeMinute}
                    error={shiftFormik.errors.breakTimeMinute}
                  />
                </div>
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  fontWeight: 500,
                }}
              >
                Preview:{" "}
                <span style={{ color: "#10b981", fontWeight: 700 }}>
                  {shiftFormik.values.breakTimeHour}:{shiftFormik.values.breakTimeMinute}:00
                </span>
              </div>
            </div>

            {/* Half Day Time */}
            <div
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 14,
                padding: "16px 18px",
              }}
            >
              <label
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: "rgba(99,102,241,0.15)",
                  }}
                >
                  <Clock size={13} strokeWidth={2.5} style={{ color: "#6366f1" }} />
                </span>
                {shiftSettingLabel.HALFDAYTIME}
              </label>
              <BaseSelect
                name="halfDayHour"
                label="Hours"
                options={halfDayHourOptions}
                placeholder="Select hours"
                handleChange={(field, value) => shiftFormik.setFieldValue(field, value)}
                handleBlur={() => shiftFormik.setFieldTouched("halfDayHour", true)}
                value={shiftFormik.values.halfDayHour}
                touched={shiftFormik.touched.halfDayHour}
                error={shiftFormik.errors.halfDayHour}
              />
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  fontWeight: 500,
                }}
              >
                Preview:{" "}
                <span style={{ color: "#6366f1", fontWeight: 700 }}>
                  {shiftFormik.values.halfDayHour}hour
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 }}>
            {!isOnboarding && (
              <BaseButton color="cancel" type="button" onClick={() => navigate("/ShiftSetting")}>
                Cancel
              </BaseButton>
            )}
            <BaseButton color="primary" type="submit">
              {id ? shiftSettingLabel.EditShift : shiftSettingLabel.ADDSHIFT}
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditShift;
